import { forwardRef } from 'react'
import { Pressable, Text, View, type GestureResponderEvent, type ViewProps } from 'react-native'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/utils/cn'

const viewVariants = cva('inline-flex flex-row items-center justify-center gap-x-2 border border-transparent', {
  variants: {
    variant: {
      solid: '',
      outline: '',
      subtle: '',
      flat: ''
    },
    intent: {
      primary: '',
      neutral: ''
    },
    size: {
      lg: 'min-h-[52px] rounded-xl px-5 py-3.5',
      md: 'min-h-10 rounded-lg px-2.5 py-3',
      sm: 'min-h-7 rounded-lg px-1.5 py-2'
    }
  },
  compoundVariants: [
    {
      variant: 'solid',
      intent: 'primary',
      className: 'bg-primary'
    },
    {
      variant: 'outline',
      intent: 'primary',
      className: 'border-primary bg-primary-flat'
    },
    {
      variant: 'subtle',
      intent: 'primary',
      className: 'bg-primary-subtle'
    },
    {
      variant: 'flat',
      intent: 'primary',
      className: 'bg-primary-flat'
    },
    {
      variant: 'solid',
      intent: 'neutral',
      className: 'border-border-10 bg-background-primary'
    },
    {
      variant: 'outline',
      intent: 'neutral',
      className: 'border-border-20 bg-fill-flat'
    },
    {
      variant: 'subtle',
      intent: 'neutral',
      className: 'bg-fill-subtle'
    },
    {
      variant: 'flat',
      intent: 'neutral',
      className: 'bg-fill-flat'
    }
  ],
  defaultVariants: {
    variant: 'solid',
    intent: 'primary',
    size: 'lg'
  }
})

const textVariants = cva('font-medium text-text-secondary', {
  variants: {
    variant: {
      solid: '',
      outline: '',
      subtle: '',
      flat: ''
    },
    intent: {
      primary: '',
      neutral: ''
    },
    size: {
      lg: 'text-base',
      md: 'text-sm',
      sm: 'text-xs'
    }
  },
  compoundVariants: [
    {
      variant: 'solid',
      intent: 'primary',
      className: 'text-text-onColor'
    },
    {
      variant: 'outline',
      intent: 'primary',
      className: 'text-primary'
    },
    {
      variant: 'subtle',
      intent: 'primary',
      className: 'text-primary'
    },
    {
      variant: 'flat',
      intent: 'primary',
      className: 'text-primary'
    }
  ],
  defaultVariants: {
    variant: 'solid',
    intent: 'primary',
    size: 'lg'
  }
})

type ButtonVariants = VariantProps<typeof viewVariants | typeof textVariants>

function getActiveClassName(variant: ButtonVariants['variant'], intent: ButtonVariants['intent']) {
  const className = {
    primary: {
      solid: 'bg-primary-active',
      outline: 'bg-primary-flat-active',
      subtle: 'bg-primary-subtle-active',
      flat: 'bg-primary-flat-active'
    },
    neutral: {
      solid: 'bg-fill-flat-active',
      outline: 'bg-fill-flat-active',
      subtle: 'bg-fill-subtle-active',
      flat: 'bg-fill-flat-active'
    }
  }
  return className[intent ?? 'primary'][variant ?? 'solid']
}

interface ButtonProps extends ViewProps, ButtonVariants {
  leftElement?: React.ReactElement
  rightElement?: React.ReactElement
  loading?: boolean
  disabled?: boolean
  onPress?: (ev: GestureResponderEvent) => void
}

export const Button = forwardRef<View, ButtonProps>(
  (
    {
      className,
      children,
      leftElement,
      rightElement,
      variant,
      intent,
      size,
      loading,
      disabled,
      onPress,
      accessible,
      accessibilityLabel,
      accessibilityRole,
      accessibilityState,
      testID,
      ...props
    },
    ref
  ) => (
    <Pressable
      ref={ref}
      className={cn('outline-offset-4', className)}
      onPress={onPress}
      disabled={disabled || loading}
      accessible={accessible ?? true}
      accessibilityLabel={accessibilityLabel ?? (typeof children === 'string' ? children : undefined)}
      accessibilityRole={accessibilityRole ?? 'button'}
      accessibilityState={{ ...accessibilityState, disabled: Boolean(disabled || loading) }}
      testID={testID}
    >
      {({ pressed }) => (
        <View
          className={cn(
            viewVariants({ variant, intent, size }),
            disabled && 'opacity-30',
            pressed && getActiveClassName(variant, intent)
          )}
          {...props}
        >
          <>
            {leftElement}
            {children && <Text className={cn(textVariants({ variant, intent, size }))}>{children}</Text>}
            {rightElement}
          </>
        </View>
      )}
    </Pressable>
  )
)
Button.displayName = 'Button'
